import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonFunctions } from '@shared/common-functions';
import { CONTACT_ERR_MSG, EMAIL_VALIDATION_PATTERN, Invalid_IMAGE_FORMAT, MULTILINGUAL_NAME_VALIDATION_PATTERN, NAME_VALIDATION_PATTERN, NO_IMAGE, USER_ROLES } from '@shared/constant/shared.constant';
import { FileResponse } from '@core/models/file-response.model';
import { DialogControlService } from '@shared/services/dialog-control.service';
import { ToastService } from '@shared/services/toast.service';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input-gg';
import { ProfileService } from '@core/services/profile.service';
import { SuperAdminService } from '@core/services/super-admin.service';
import { autoUnsubWith } from '@shared/services/utility.abstract';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';
import { ConfigService } from '@core/services/config.service';

@Component({
  selector: 'app-group-oprator-basic-information',
  templateUrl: './group-oprator-basic-information.component.html',
  styleUrls: ['./group-oprator-basic-information.component.scss']
})
export class GroupOpratorBasicInformationComponent implements  OnChanges {
  readonly destroyRef = inject(DestroyRef);
  @Input() isProfileImgError: boolean = false;
  @Input() showInput: boolean = false;
  @Input() imageUrl: string;
  @Input() driverDetails: any;
  @Output() driverBesicData = new EventEmitter<any>();
  driverUpdateForm: FormGroup;
  NAME_MAX_LENGTH: number = 50;
  dummy = NO_IMAGE;
  searchCountryField = SearchCountryField;
  countryISO: CountryISO = CountryISO.UnitedStates;
  phoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedKingdom];
  separateDialCode = false;
  formData: FormData = new FormData();
  allowedExtensions = ['jpg', 'jpeg', 'png'];
  role: string = this.encryptStorage.getItem('roleName');
  userRoles = USER_ROLES;
  requiredLastNameAsterisk: boolean = false;


  constructor(
    private readonly fb: FormBuilder,
    public readonly dialogControlService: DialogControlService,
    private readonly superAdminService: SuperAdminService,
    private readonly toastService: ToastService,
    private readonly service: ProfileService,
    private readonly encryptStorage: EncryptionStorageService,
    private readonly configService: ConfigService
  ) {
    const { validators, required } = CommonFunctions.getLastNameValidators('groupOperator', this.configService);
    this.requiredLastNameAsterisk = required;
    this.driverUpdateForm = this.fb.group({
      id: [''],
      firstName: ['', [Validators.required, Validators.pattern(MULTILINGUAL_NAME_VALIDATION_PATTERN), Validators.minLength(3), Validators.maxLength(this.NAME_MAX_LENGTH), CommonFunctions.noLeadingSpacesValidator()]],
      lastName: ['', [...validators,Validators.pattern(MULTILINGUAL_NAME_VALIDATION_PATTERN), Validators.minLength(3), Validators.maxLength(this.NAME_MAX_LENGTH), CommonFunctions.noLeadingSpacesValidator()]],
      phone: ['',[Validators.required]],
      profileImagePath: [''],
      companyName:['',[Validators.required, Validators.minLength(3), CommonFunctions.noLeadingSpacesValidator()]],
      groupName:['',[Validators.required, Validators.minLength(3), CommonFunctions.noLeadingSpacesValidator()]],
      dotNumber:['',[Validators.minLength(6)]],
      address:['',[ Validators.minLength(3), CommonFunctions.noLeadingSpacesValidator()]],
      country: ['',[ Validators.pattern(NAME_VALIDATION_PATTERN), Validators.minLength(3), Validators.maxLength(50), CommonFunctions.noLeadingSpacesValidator()]],
      city: ['',[ Validators.pattern(NAME_VALIDATION_PATTERN), Validators.minLength(3), Validators.maxLength(50), CommonFunctions.noLeadingSpacesValidator()]],
      zipCode: ['',[ Validators.pattern('^[0-9-]*$'), Validators.minLength(5), Validators.maxLength(10)]],
      aboutGroup: [''],
      email: ['', [Validators.required, Validators.pattern(EMAIL_VALIDATION_PATTERN)]]
    });
  }
  hasValidCredits(): boolean {
    return this.driverDetails?.userCredits?.some((c: any) => c.amount > 0) ?? false;
  }
  ngOnChanges() {
    if (this.showInput) {
      this.patchForm(this.driverDetails)
    }
  }

  triggerFileInput() {
    const fileInput = document.getElementById('fileInput') as HTMLElement;
    fileInput.click();
  };

  getFlagClass(iso: string): string {
    const lowerCaseIso = iso?.toLowerCase();
    return `fi fi-${lowerCaseIso}`;
  };

  clearImage() {
    this.imageUrl = null;
    this.driverUpdateForm.get('profileImagePath')?.setValue(null);
    this.formData.delete('Images');
    this.isProfileImgError = false;
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }

  }

  getFormattedMobileNumber(): string {
    if (!this.driverDetails?.mobileNumber || !this.driverDetails?.iso) {
      return '';
    }

    let mobileNumber = this.driverDetails.mobileNumber;
    let countryCode = this.driverDetails.countryCode;

    if (mobileNumber.startsWith(countryCode)) {
      mobileNumber = mobileNumber.replace(countryCode, '').trim();
    }

    return mobileNumber;
  }

onlyNumberInput(event: KeyboardEvent): void {
    CommonFunctions.onlyNumberInput(event);
  }

  onImageSelected(event: any) {
    const file = event.target.files[0];
    const maxSize = 5 * 1024 * 1024;
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    this.formData.append('Images', file);

    if (!fileExtension || !this.allowedExtensions.includes(fileExtension)) {
      this.toastService.showToastMessage('error', 'Error', Invalid_IMAGE_FORMAT);
      this.formData.delete('Images');
      return false;
    } else if (file.size > maxSize && (fileExtension || this.allowedExtensions.includes(fileExtension))) {
      this.toastService.showToastMessage('error', 'Error', 'File size should be less than 5MB.');
      this.formData.delete('Images');
      return false;
    }

    if (file.size < maxSize && (fileExtension || this.allowedExtensions.includes(fileExtension))) {

      this.service.imageUplaod(this.formData)
        .pipe(autoUnsubWith(this.destroyRef)).subscribe({
          next: (response: FileResponse) => {
            this.formData.delete('Images')
            const data = response;
            for (const key in data) {
              if (data.hasOwnProperty(key)) {
                const url = data[key];
                this.imageUrl = url;
                this.driverUpdateForm.get('profileImagePath')?.setValue(url);

              }
            }
          }, error: (_error) => {
            this.isProfileImgError = false;
          }
        });
    }

    return true;
  };
  patchForm(driverDetails: any) {
    this.countryISO = driverDetails?.iso ?? 'us';
    if (driverDetails) {
      this.driverUpdateForm.patchValue({
        firstName: driverDetails.firstName || '',
        lastName: driverDetails.lastName || '',
        phone: driverDetails.mobileNumber || '',
        profileImagePath: driverDetails.profileImagePath || "",
        groupName:driverDetails.groupName || "",
        companyName: driverDetails?.companyName || driverDetails?.groupName || "",
        dotNumber:driverDetails.dotNumber || "",
        address:driverDetails.address || "",
        country:driverDetails.country || "",
        city: driverDetails.city || "",
        zipCode: driverDetails.zipCode || "",
        aboutGroup:driverDetails.aboutGroup || "",
        email:driverDetails.email || ""

      })

    }
  };
  calcelDriverInfoEdit(driverDetails) {
    this.showInput = false;
    this.superAdminService.setShowInput(false);
    this.driverUpdateForm.reset();
    this.patchForm(driverDetails);
    this.imageUrl = driverDetails.profileImagePath;
  };

  updateDriverInformation() {
    if (!this.driverUpdateForm.value.email && !this.driverUpdateForm.value.phone) {
      this.toastService.showToastMessage('error', 'Error', CONTACT_ERR_MSG);
      return;
    }
    this.driverUpdateForm.get('groupName')?.setValue(this.driverUpdateForm.get('companyName')?.value || '');
    if(this.driverUpdateForm.valid){
      const groupUserProfileData = this.driverUpdateForm.value;
      const checkData ={
          isGroupUser:true
      }
      groupUserProfileData.groupName = groupUserProfileData.companyName;
      const dataToEmit ={ ...groupUserProfileData, ...checkData}
      this.driverBesicData.emit(dataToEmit)
    }else{
      this.driverUpdateForm.markAllAsTouched();
    }

  }
  onKeyDown(event: KeyboardEvent): void {
    CommonFunctions.onKeyDown(event);
  }

}
